import { Ref, forwardRef, useEffect, useState } from "react";
import { Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  DataGridPro,
  GridColDef,
  GridRowsProp,
  GridEditInputCell,
  GridRowModel,
  GridRenderEditCellParams,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { randomId } from "@mui/x-data-grid-generator";
import { useTranslation } from "react-i18next";

import { ErrorTooltip } from "../../../common/components/styled/tooltips/StyledTooltips";
import { GridActionCell } from "./GridActionCell";
import { RoundedButton } from "../../../common/components/styled/styled";

interface Props {
  columns: GridColDef[];
  rows: GridRowsProp;
  newRow?: {};
  onSave?: (data: any) => void;
  onUndo?: () => void;
  hideSave: boolean;
  onOpenRow?: (row: any) => void;
  actions?: string[];
  disableScrollToBottomOnAddRow?: boolean;
}

export const DataGrid = ({
  columns: columnsData,
  rows: originalRows,
  newRow,
  onSave,
  onUndo,
  hideSave,
  onOpenRow,
  actions,
  disableScrollToBottomOnAddRow,
}: Props) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<GridRowsProp>(originalRows);
  const apiRef = useGridApiRef();

  const [disableButton, setDisableButton] = useState<boolean>(true);

  const EditInputCell = forwardRef(
    (props: GridRenderEditCellParams, ref: Ref<HTMLInputElement>) => {
      const { error } = props;
      const title = typeof error === "string" ? error : "";
      return (
        <ErrorTooltip open={!!error} title={title ? title : false}>
          <Box ref={ref}>
            <GridEditInputCell {...props} error={!!error} />
          </Box>
        </ErrorTooltip>
      );
    },
  );

  const editableColumns = columnsData.map((column) => {
    return {
      ...column,
      renderEditCell: (params: GridRenderEditCellParams) => {
        return <EditInputCell {...params} key={params.id} />;
      },
    };
  });

  const deleteRow = (row: GridRowModel) => {
    // If row is not saved to server, it can be removed altogether
    row.status === "new"
      ? setRows(rows.filter((r) => r.id !== row.id))
      : setRows(
          rows.map((r) =>
            r.id === row.id ? { ...row, status: "deleted" } : r,
          ),
        );
  };

  const openRow = (row: GridRowModel) => {
    if (onOpenRow !== undefined) {
      onOpenRow(row);
    }
  };

  const deleteRowCell = (row: any) => (
    <GridActionCell
      label="delete"
      rowAction={() => deleteRow(row)}
      icon={<DeleteIcon />}
    />
  );

  const openRowCell = (row: any) => (
    <GridActionCell
      label="open"
      rowAction={() => openRow(row)}
      icon={<LaunchIcon />}
    />
  );

  const columns: GridColDef[] = [
    ...editableColumns,
    {
      field: "actions",
      type: "actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ row }) => {
        const cells = [];
        if (actions?.includes("open")) {
          cells.push(openRowCell(row));
        }
        if (actions?.includes("delete")) {
          cells.push(deleteRowCell(row));
        }
        return cells;
      },
    },
  ];

  const addRow = () => {
    const row = { ...newRow, id: randomId(), status: "new" };
    setRows([...rows, row]);

    if (disableScrollToBottomOnAddRow) return;

    // Scroll to the new row
    // Wait for the grid to render the new row before scrolling
    setTimeout(() => {
      apiRef.current.scrollToIndexes({
        rowIndex: rows.length,
        colIndex: 0,
      });
    }, 200);
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    // Keep 'new' status for rows not yet sent to server
    const updatedRow = { ...newRow, status: "updated" };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const save = () => {
    if (onSave) {
      onSave(rows);
      setDisableButton(true);
    }
  };

  const undo = () => {
    if (onUndo) {
      onUndo();
      setDisableButton(true);
    }
  };

  useEffect(() => {
    const isChanged = rows.find((row) => row.status === "updated");

    if (isChanged) setDisableButton(false);
  }, [rows]);

  useEffect(() => {
    setRows(originalRows);
  }, [originalRows]);

  return (
    <Box sx={{ marginTop: 3 }} maxWidth="80vw">
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {newRow ? (
          <RoundedButton onClick={addRow}>
            {t("add", { ns: "common" })}
          </RoundedButton>
        ) : null}

        {!hideSave ? (
          <Box>
            <RoundedButton
              variant="contained"
              onClick={save}
              disabled={disableButton}
            >
              {t("save")}
            </RoundedButton>
            <RoundedButton
              variant="outlined"
              onClick={undo}
              disabled={disableButton || !onUndo}
            >
              {t("undo")}
            </RoundedButton>
          </Box>
        ) : null}
      </Box>
      <Box
        style={{ height: 450, width: "100%" }}
        sx={{
          "& .cell-new": {
            bgcolor: "rgba(60, 179, 113, 0.8)",
            "&:hover": { bgcolor: "rgba(60, 179, 113, 1)" },
          },
          "& .cell-updated": {
            fontStyle: "italic",
          },
          "& .cell-deleted": {
            bgcolor: "rgba(255, 99, 71, 0.8)",
            textDecoration: "line-through",
            "&:hover": { bgcolor: "rgba(255, 99, 71, 1)" },
          },
        }}
      >
        <DataGridPro
          editMode="row"
          rows={rows}
          columns={columns}
          processRowUpdate={processRowUpdate}
          disableRowSelectionOnClick
          getRowClassName={(params) => `cell-${params.row.status}`}
          apiRef={apiRef}
        />
      </Box>
    </Box>
  );
};
